/**
 * Shared Texture Cache
 * Global cache for Data3DTexture arrays to prevent recreating them
 * Used by both preload system and material creation
 */


// Global cache for Data3DTexture arrays keyed by LOD level and type
const textureArrayCache = new Map()
// Size = (earth LOD levels × 5 texture types) + (moon LOD levels × 1 type)
// Currently: 2 earth LODs × 5 = 10, + 2 moon × 1 = 2, total = 12. Set headroom above that.
const MAX_CACHE_SIZE = 30

/**
 * Get a Data3DTexture array from cache
 * @param {string} lodLevel - LOD level (e.g., 'LOD_0')
 * @param {string} textureType - Texture type (e.g., 'diffuse')
 * @returns {Data3DTexture|null}
 */
export function getCachedTextureArray(lodLevel, textureType) {
  const key = `${lodLevel}-${textureType}`
  return textureArrayCache.get(key) || null
}

/**
 * Set a Data3DTexture array in cache
 * @param {string} lodLevel - LOD level (e.g., 'LOD_0')
 * @param {string} textureType - Texture type (e.g., 'diffuse')
 * @param {Data3DTexture} textureArray - The texture array to cache
 */
export function setCachedTextureArray(lodLevel, textureType, textureArray) {
  const key = `${lodLevel}-${textureType}`
  
  textureArrayCache.set(key, textureArray)
  
  // Evict oldest entries if cache is too large
  if (textureArrayCache.size > MAX_CACHE_SIZE) {
    const firstKey = textureArrayCache.keys().next().value
    const oldTexture = textureArrayCache.get(firstKey)
    
    if (oldTexture && oldTexture.dispose) {
      oldTexture.dispose()
    }
    
    textureArrayCache.delete(firstKey)
  }
  
  return textureArray
}

/**
 * Check if a texture array is cached
 * @param {string} lodLevel - LOD level (e.g., 'LOD_0')
 * @param {string} textureType - Texture type (e.g., 'diffuse')
 * @returns {boolean}
 */
export function hasTextureArray(lodLevel, textureType) {
  const key = `${lodLevel}-${textureType}`
  return textureArrayCache.has(key)
}

/**
 * Clear all cached texture arrays
 */
export function clearTextureCache() {
  textureArrayCache.forEach((texture) => {
    if (texture && texture.dispose) {
      texture.dispose()
    }
  })
  textureArrayCache.clear()
}

/**
 * Get cache statistics
 */
export function getCacheStats() {
  let totalMemoryMB = 0
  let count = 0
  
  textureArrayCache.forEach((texture, key) => {
    if (texture && texture.image) {
      const memoryMB = (texture.image.width * texture.image.height * texture.image.depth * 4) / (1024 * 1024)
      totalMemoryMB += memoryMB
      count++
    }
  })
  
  return {
    count,
    totalMemoryMB: totalMemoryMB.toFixed(1),
    keys: Array.from(textureArrayCache.keys())
  }
}
